import trim from 'lodash/trim';
import map from 'lodash/map';
import filter from 'lodash/filter';
import get from 'lodash/get';
import some from 'lodash/some';
import find from 'lodash/find';
import flatten from 'lodash/flatten';
import has from 'lodash/has';
import maxBy from 'lodash/maxBy';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import {
    selectProductDetailsWrapper,
    selectInventoryWrapper,
    selectProductOfferingWrapper,
    selectSelectedQuantityWrapper,
} from '../selectors/PdpNameSpaceSelector';
import { selectedProductSkus } from '../selectors/ProductOptionsSelector';
import { bopisIncentiveProductEligibilitySelector } from '../selectors/ProductPricingSelector';

import { selectFeatureFlags } from '../selectors/ContextSelector';

import {
    KEY_FEATURE_CONSTS_MIN_ICONS_COUNT,
    KEY_FEATURE_CONSTS_MAX_ICONS_COUNT,
} from '../common/Constants';

import productConstants from '../common/ProductConstants';

let originalPricingCadence = 'regular';

export const decimalRounding = (value) => parseFloat(Math.round(value * 100) / 100).toFixed(2);
/* istanbul ignore next */
const getDepartmentId = (category) => {
    if (has(category, 'parent')) {
        return getDepartmentId(category.parent);
    }
    return category.id;
};

const getPricingCadence = (type, marketingLabel, promotions) => {
    let cadence = 'regular';

    if (type && type.length !== 0) {
        cadence = type;
    } else if (marketingLabel || promotions) {
        cadence = 'buy more and save';
    }

    return cadence;
};

/* istanbul ignore next */
const getStoreAvailability = (storeAvailability) => {
    let storeValue = '';
    if (storeAvailability[0] === 'Omnichannel') {
        storeValue = 'both';
    } else if (storeAvailability[0] === 'OnlineOnly') {
        storeValue = 'online only';
    }
    return storeValue;
};

const getQuantityDiscount = (orderItem) => {
    let quantityDiscount = '0.00';
    /* istanbul ignore next */
    if (orderItem.adjustments && orderItem.adjustments.length) {
        const nForDiscount = find(orderItem.adjustments, { code: 'NFORDISCOUNT' });
        quantityDiscount =
            nForDiscount && nForDiscount.amount ? nForDiscount.amount.toFixed(2) : '';
    }
    return quantityDiscount;
};

const getPriceAfterDiscount = (orderItem) => {
    let priceDiscount = '0.00';
    /* istanbul ignore next */
    if (orderItem.totals && orderItem.totals.length) {
        const nForDiscountPrice = find(orderItem.totals, { name: 'ITEM AMOUNT' });
        priceDiscount =
            nForDiscountPrice && nForDiscountPrice.amount
                ? nForDiscountPrice.amount.toFixed(2)
                : '';
    }
    return priceDiscount;
};

const getProductPricing = (defaults, original, orderItem) => {
    const productPricing = {
        sellingPrice: '0.00',
        listPrice: '0.00',
    };
    /* istanbul ignore next */
    if (!isEmpty(orderItem) && orderItem.prices) {
        const listPrice = find(orderItem.prices, { type: 'ORIGINAL' });
        const sellingPrice = find(orderItem.prices, { label: 'ACTIVE' });
        productPricing.sellingPrice =
            sellingPrice && sellingPrice.amount ? Number(sellingPrice.amount).toFixed(2) : '0.00';
        productPricing.listPrice =
            listPrice && listPrice.amount ? Number(listPrice.amount).toFixed(2) : '0.00';
    } else {
        productPricing.sellingPrice =
            defaults && defaults.length
                ? decimalRounding(trim(defaults.split('- ')[defaults.split('- ').length - 1], '$'))
                : '0.00';
        productPricing.listPrice =
            original && original.length
                ? decimalRounding(trim(original.split('- ')[original.split('- ').length - 1], '$'))
                : productPricing.sellingPrice;
    }
    return productPricing;
};

const setProductAnalytics = (
    productDetails,
    Pricing,
    collectionView,
    saleAndClearence,
    urgencyStatements,
    contextAttributes,
    selectedProductSKU,
    bopisIncentiveProductEligibility,
    featureFlags,
    sourceMiniPDP
) => {
    const {
        id,
        name,
        lots,
        category,
        restrictions,
        collection,
        valuation,
        hybridIndicator,
        brand,
    } = productDetails;
    const { defaults, original, type, marketingLabel, surcharges, promotions } = Pricing;
    const firstLot = lots.length && lots[0];
    const productCurrentSellingPrice =
        defaults && defaults.length
            ? decimalRounding(trim(defaults.split('- ')[defaults.split('- ').length - 1], '$'))
            : '0.00';
    const productListPrice =
        original && original.length
            ? decimalRounding(trim(original.split('- ')[original.split('- ').length - 1], '$'))
            : productCurrentSellingPrice;
    const giftRegistryIndicator = restrictions && restrictions.canAddToRegistry ? 'yes' : 'no';
    const productMarketingLabel = !isEmpty(marketingLabel)
        ? marketingLabel.toLowerCase()
        : 'no marketing label';
    const productAverageRating =
        valuation && valuation.rating ? decimalRounding(`${valuation.rating}`) : '';
    const productReviews =
        valuation && valuation.reviews && valuation.reviews.count
            ? `${valuation.reviews.count}`
            : '';
    /* eslint-disable no-nested-ternary */
    const categoryID = category && category.id ? category.id : '';
    const departmentID = category && category.parent ? getDepartmentId(category) : '';
    const productCollectionFlag = collection && collection.length ? 'yes' : 'no';
    const productCollectionView = collectionView ? 'yes' : 'no';
    const productCarePlanFlag = firstLot && firstLot.warranties ? 'yes' : 'no';
    const productStoreAvailability = getStoreAvailability(map(lots, 'channelOffering'));
    const productUrgencyMessageShown =
        urgencyStatements &&
        (urgencyStatements.viewMessage ||
            urgencyStatements.addToCartMessage ||
            urgencyStatements.soldMessage)
            ? 'yes'
            : 'no';

    const productType = get(contextAttributes, '[PRODUCT TYPE].values[0]', '').toLowerCase();
    const productItemType = get(contextAttributes, '[ITEM TYPE].values[0]', '').toLowerCase();
    const productBrandName = get(brand, 'name', '').toLowerCase();
    const productCategoryName = get(category, 'name', '');
    const productDivisionID = get(firstLot, 'organization.division', '');
    const productEntity = get(firstLot, 'organization.entity', '');
    const productSubdivision = get(firstLot, 'organization.subdivision', '');
    const lotChannelOffering = get(selectedProductSKU, '[0].channelOffering', '');
    const isBopisIncentiveEligible =
        productDetails.bopisEligibility &&
        lotChannelOffering !== 'OnlineOnly' &&
        bopisIncentiveProductEligibility.atleastOneLotEligible;
    const featureValues = get(firstLot, 'productIcons', []);

    /* istanbul ignore next */
    let productFeatureAttributes = flatten(featureValues.map((item) => item.valueIcons)) || [];
    const productFeatureAttributesLength = productFeatureAttributes.length;
    if (productFeatureAttributesLength >= KEY_FEATURE_CONSTS_MIN_ICONS_COUNT) {
        /* istanbul ignore next */
        productFeatureAttributes =
            productFeatureAttributesLength >= KEY_FEATURE_CONSTS_MAX_ICONS_COUNT
                ? productFeatureAttributes.slice(0, KEY_FEATURE_CONSTS_MAX_ICONS_COUNT)
                : productFeatureAttributes;
        productFeatureAttributes = productFeatureAttributes.map((item) => item.value).join('|');
    }

    let rebatesInfo = 'no';

    lots.map(({ items }) => {
        const rebatesAvailable = some(items, { rebatesAvailable: true });
        /* istanbul ignore if */
        if (rebatesAvailable) {
            rebatesInfo = 'yes';
        }

        return null;
    });

    originalPricingCadence = getPricingCadence(type, marketingLabel, promotions);

    const productInfo = {
        productAverageRating,
        productCurrentSellingPrice,
        productListPrice,
        productName: name
            .toLowerCase()
            .replace(/[^a-zA-Z0-9]/g, ' ')
            .replace(/\s{2,}/g, ' '),
        productLotId: map(lots, 'id'),
        productPPID: id,
        /* istanbul ignore next */
        productPricingCadence: hybridIndicator ? 'hybrid' : originalPricingCadence,
        productReviews,
        productStoreAvailability,
        productCategoryID: categoryID,
        productDepartmentID: departmentID,
        productSurchargePrice: surcharges && surcharges.length ? surcharges : '0.00',
        productEcoRebatesFlag: rebatesInfo,
        productCollectionFlag,
        productCollectionView,
        productMarketingLabel,
        productCarePlanFlag,
        productType,
        productItemType,
        productBrandName,
        productCategoryName,
        productDivisionID,
        productEntity,
        productSubdivision,
        productFeatureAttributes,
    };
    let appendInfo;
    /* istanbul ignore next */
    if (sourceMiniPDP) {
        appendInfo = {
            productGiftRegistryIndicator: giftRegistryIndicator,
        };
    } else {
        appendInfo = {
            productUrgencyMessageShown,
        };
    }

    if (isBopisIncentiveEligible) {
        appendInfo = {
            ...appendInfo,
            bopisIncentivePP: 'yes',
        };
    }

    Object.assign(productInfo, appendInfo);
    const product = [
        {
            productInfo,
        },
    ];

    return product;
};

/* istanbul ignore next */
const setBundleProductAnalytics = (productDetails, Pricing, collectionView) => {
    const { id, name, collection, valuation, category } = productDetails;
    const calcChildLevelPrice = Pricing ? values(Pricing.calcChildLevelPrice) : '';
    const calcBundleLevelPrice = Pricing ? Pricing.calcBundleLevelPrice : '';
    const { marketingLabel, type, surcharges } = calcChildLevelPrice
        ? Pricing.calcBundleLevelPrice
        : '';
    const products = map(productDetails.products, 'product');
    const flattenLots = flatten(map(products, 'lots'));
    const productCollectionFlag = collection && collection.length ? 'yes' : 'no';
    const productCollectionView = collectionView ? 'yes' : '';
    const productAverageRating =
        valuation && valuation.rating ? decimalRounding(`${valuation.rating}`) : '';
    const productReviews =
        valuation && valuation.reviews && valuation.reviews.count
            ? `${valuation.reviews.count}`
            : '';
    const defaultsMaxValue = maxBy(calcChildLevelPrice, 'defaultsMax');
    const originalMaxValue = calcBundleLevelPrice.originalMax
        ? calcBundleLevelPrice.originalMax
        : null;
    const categoryID = category && category.id ? category.id : '';
    const departmentID = category && category.parent ? getDepartmentId(category) : '';
    const productMarketingLabel = !isEmpty(marketingLabel)
        ? marketingLabel.toLowerCase()
        : 'no marketing label';
    const productInfo = {
        productName: name
            .toLowerCase()
            .replace(/[^a-zA-Z0-9]/g, ' ')
            .replace(/\s{2,}/g, ' '),
        productPPID: id,
        productCurrentSellingPrice: defaultsMaxValue
            ? decimalRounding(defaultsMaxValue.defaultsMax).toString()
            : '0.00',
        productListPrice: originalMaxValue ? decimalRounding(originalMaxValue).toString() : '0',
        productLotId: map(flattenLots, 'id'),
        productCollectionFlag,
        productCollectionView,
        productAverageRating,
        productReviews,
        productPricingCadence: getPricingCadence(type, marketingLabel),
        productSurchargePrice: surcharges && surcharges.length ? surcharges : '0.00',
        productCategoryID: categoryID,
        productDepartmentID: departmentID,
        productType: 'bundle',
        productItemType: 'bundle',
        productMarketingLabel,
        productStoreAvailability: 'online only',
    };
    const product = [
        {
            productInfo,
        },
    ];
    return product;
};

const setCartAddAnalytics = (
    productDetails,
    pricing,
    productOptions,
    selectedQuantity,
    inventory,
    productOffering,
    deliveryMethod,
    clickPosition,
    orderItem,
    maxQuantityMsgDisplay,
    deliveryDayType,
    deliveryDays,
    featureFlags,
    virtualTryOn
) => {
    const { id, name, lots } = productDetails;
    const { defaults, original, type, surcharges, marketingLabel, promotions } = pricing;
    const productLotId = filter(lots, (obj) =>
        some(obj.items, { id: productOptions.filteredItems[0].id })
    )[0].id;
    const pricingObj = getProductPricing(defaults, original, orderItem);
    const productCurrentSellingPrice = pricingObj.sellingPrice;
    const productListPrice = pricingObj.listPrice;
    const productStoreAvailability = getStoreAvailability(map(lots, 'channelOffering'));
    /* istanbul ignore next */
    const productInStock =
        inventory && inventory.length && inventory[0].inventory
            ? some(inventory[0].inventory, { atp: true })
                ? 'in stock'
                : 'out of stock'
            : '';
    const productDeliveryMessage =
        productOffering && productOffering.delivery && productOffering.delivery.message
            ? productOffering.delivery.message
            : '';
    const isBopisIncentiveEligible = !!(
        productOffering.pricing &&
        productOffering.pricing.bopis &&
        deliveryMethod.toLowerCase() === productConstants.ATC_BOPIS_SELECTED_MESSAGE &&
        featureFlags &&
        featureFlags.enableBopisIncentive
    );
    const productEstimatedDelivery = productDeliveryMessage.replace(
        '<strong>est. delivery:</strong> ',
        ''
    );
    const productQuantityDiscount = getQuantityDiscount(orderItem);
    const productPriceAfterDiscount = getPriceAfterDiscount(orderItem);
    let productInfo = {
        productPPID: id,
        productLotId: [productLotId],
        productSKU: productOptions.filteredItems[0].id,
        productName: name
            .toLowerCase()
            .replace(/[^a-zA-Z0-9]/g, ' ')
            .replace(/\s{2,}/g, ' '),
        productListPrice,
        productCurrentSellingPrice,
        productStoreAvailability,
        /* istanbul ignore next */
        productQuantity:
            selectedQuantity !== '' && selectedQuantity !== 0 ? `${selectedQuantity}` : '1',
        productPricingCadence: getPricingCadence(type, marketingLabel, promotions),
        productDeliveryMethod: deliveryMethod.toLowerCase(),
        productInStock,
        productCouponAmount: '0.00',
        productSurchargePrice: surcharges && surcharges.length ? surcharges : '0.00',
        productQuantityDiscount,
        productPriceAfterDiscount,
        productEstimatedDelivery,
        productBundleFlag: 'no',
        clickPosition,
        productMaxQuantityReached: maxQuantityMsgDisplay ? 'yes' : 'no',
        productDeliveryDays: deliveryDays,
        productDeliveryDayType: deliveryDayType,
    };
    if (isBopisIncentiveEligible) {
        const updatedProductInfo = {
            ...productInfo,
            productBopisIncentive: 'yes',
        };
        productInfo = updatedProductInfo;
    }
    const virtualTryOnEnabled = get(virtualTryOn, 'enabled');
    if (virtualTryOnEnabled !== undefined) {
        productInfo = {
            ...productInfo,
            vendorCartAdd: 'revieve:vto-pdp',
        };
    }
    return productInfo;
};

const setCartUpdateAnalytics = (
    productDetails,
    pricing,
    productOptions,
    selectedQuantity,
    inventory,
    productOffering,
    silverBundle,
    payload,
    featureFlags
) => {
    const { id, name, lots } = productDetails;
    const { defaults, original, type, surcharges, marketingLabel, promotions } = pricing;
    const productLotId = filter(lots, (obj) =>
        some(obj.items, { id: productOptions.filteredItems[0].id })
    )[0].id;
    const productCurrentSellingPrice =
        defaults && defaults.length
            ? decimalRounding(trim(defaults.split('- ')[defaults.split('- ').length - 1], '$'))
            : '0.00';
    const productListPrice =
        original && original.length
            ? decimalRounding(trim(original.split('- ')[original.split('- ').length - 1], '$'))
            : productCurrentSellingPrice;
    const productStoreAvailability = getStoreAvailability(map(lots, 'channelOffering'));
    const productInStock =
        inventory && inventory.length && inventory[0].inventory
            ? some(inventory[0].inventory, { atp: true })
                ? 'in stock'
                : 'out of stock'
            : '';
    const isBopisIncentiveEligible = !!(
        productOffering.pricing &&
        productOffering.pricing.bopis &&
        payload.deliveryMethod.toLowerCase() === productConstants.ATC_BOPIS_SELECTED_MESSAGE &&
        featureFlags &&
        featureFlags.enableBopisIncentive
    );

    const currentItemSKU = productOptions.filteredItems[0].id;

    let productInfo = {
        productPPID: id,
        productLotId: [productLotId],
        productSKU: currentItemSKU,
        productName: name
            .toLowerCase()
            .replace(/[^a-zA-Z0-9]/g, ' ')
            .replace(/\s{2,}/g, ' '),
        productListPrice,
        productCurrentSellingPrice,
        productStoreAvailability,
        productQuantity:
            selectedQuantity !== '' && selectedQuantity !== 0 ? `${selectedQuantity}` : '1',
        productPrevQuantity: payload.productPreviousQuantity,
        productPricingCadence: getPricingCadence(type, marketingLabel, promotions),
        productDeliveryMethod: payload.deliveryMethod.toLowerCase(),
        productInStock,
        productCouponAmount: '0.00',
        productSurchargePrice: surcharges.length ? surcharges : '0.00',
        productQuantityDiscount: '0.00',
        productBundleFlag: silverBundle && silverBundle.length ? 'yes' : 'no',
        productMaxQuantityReached: payload.maxQuantityMsgDisplay ? 'yes' : 'no',
    };
    if (isBopisIncentiveEligible) {
        const updatedProductInfo = {
            ...productInfo,
            productBopisIncentive: 'yes',
        };
        productInfo = updatedProductInfo;
    }

    const product = [
        {
            productInfo,
        },
    ];

    // commenting out this code as it will be coming back
    // as analytics fix their issue.
    /*
    if (currentItemSKU !== payload.productPreviousSku) {
        productInfo.productState = 'add';
        const previousProductInfo = getPreviousProductInfo(lots,
            payload.productPreviousSku,
            silverBundle,
            payload.maxQuantityMsgDisplay,
            payload.productPreviousQuantity,
            payload.prevDeliveryMethod,
        );
        product = product.concat({ productInfo: previousProductInfo });
    }
    */
    payload.cartItems[0].childItems.forEach((childItem) => {
        const warrantyPrice = find(productOptions.selectedProductLot[0].warranties, {
            itemId: childItem.skuId,
        });
        const lotId = (childItem.skuId || '').slice(0, 7);
        const setSilverOfferingItemsPrice =
            silverBundle && silverBundle.selectedSilverOfferingItems
                ? find(silverBundle.selectedSilverOfferingItems, { id: childItem.skuId })
                : {};
        let currentSellingPrice = '0.00';
        /* istanbul ignore next */
        if (warrantyPrice && warrantyPrice.price) {
            currentSellingPrice = Number(warrantyPrice.price).toFixed(2);
        } else if (setSilverOfferingItemsPrice && setSilverOfferingItemsPrice.price) {
            currentSellingPrice = Number(setSilverOfferingItemsPrice.price).toFixed(2);
        }
        product.push({
            productInfo: {
                productLotId: [lotId],
                productPPID: childItem.ppId,
                productQuantity: childItem.quantity,
                productSKU: childItem.skuId,
                productCurrentSellingPrice: currentSellingPrice,
            },
        });
    });
    return product;
};

const setProductOOSAnalytics = (productDetails, Pricing, productOptions) => {
    const { id, name, lots, hybridIndicator } = productDetails;
    const { defaults, original, type, surcharges, marketingLabel } = Pricing;
    const productCurrentSellingPrice =
        defaults && defaults.length
            ? decimalRounding(trim(defaults.split('- ')[defaults.split('- ').length - 1], '$'))
            : '0.00';
    const productListPrice =
        original && original.length
            ? decimalRounding(trim(original.split('- ')[original.split('- ').length - 1], '$'))
            : productCurrentSellingPrice;
    const productStoreAvailability = getStoreAvailability(map(lots, 'channelOffering'));

    const productInfo = {
        productPPID: id,
        productLotId: map(lots, 'id'),
        productSKU: get(productOptions, 'filteredItems[0].id', ''),
        productName: name
            .toLowerCase()
            .replace(/[^a-zA-Z0-9]/g, ' ')
            .replace(/\s{2,}/g, ' '),
        productCurrentSellingPrice,
        productListPrice,
        productStoreAvailability,
        productPricingCadence: hybridIndicator ? 'hybrid' : getPricingCadence(type, marketingLabel),
        productDeliveryMethod: 'ship to home',
        productInStock: 'out of stock',
        productCouponAmount: '0.00',
        productSurchargePrice: surcharges && surcharges.length ? surcharges : '0.00',
        productQuantityDiscount: '0.00',
    };
    const product = [
        {
            productInfo,
        },
    ];
    return product;
};

const getBundleEstimatedDeliveryDate = (bundleDeliveryDate, bagItems) => {
    let bundleAvailableDate = null;
    bagItems.forEach(({ skuId, ppId }) => {
        const bundleItemDeliveryInfo = bundleDeliveryDate[ppId][skuId];
        /* istanbul ignore next */
        if (bundleItemDeliveryInfo.earliestDeliveryDate) {
            if (bundleAvailableDate) {
                if (Date(bundleItemDeliveryInfo.earliestDeliveryDate) > Date(bundleAvailableDate)) {
                    bundleAvailableDate = bundleItemDeliveryInfo.earliestDeliveryDate;
                }
            } else {
                bundleAvailableDate = bundleItemDeliveryInfo.earliestDeliveryDate;
            }
        }
    });
    return bundleAvailableDate;
};

const findCartTotal = (totals) => {
    const merchandiseSubTotal = find(totals, { type: 'SUB TOTAL' });
    return merchandiseSubTotal && merchandiseSubTotal.amount
        ? merchandiseSubTotal.amount.toFixed(2)
        : '0.00';
};

class PDPAnalytics {
    static getProductAnalytics(
        store,
        pricingDetails,
        collectionView,
        saleAndClearence,
        contextAttributes,
        sourceMiniPDP
    ) {
        const { urgencyStatements } = store;
        const productDetails = selectProductDetailsWrapper(sourceMiniPDP)(store);
        const selectedProductSKU = selectedProductSkus(sourceMiniPDP)(store);
        const featureFlags = selectFeatureFlags(store);
        const bopisIncentiveProductEligibility = bopisIncentiveProductEligibilitySelector(
            sourceMiniPDP
        )(store);
        return setProductAnalytics(
            productDetails,
            pricingDetails,
            collectionView,
            saleAndClearence,
            urgencyStatements,
            contextAttributes,
            selectedProductSKU,
            bopisIncentiveProductEligibility,
            featureFlags,
            sourceMiniPDP
        );
    }

    static getBundleProductAnalytics(store, pricingDetails, collectionView) {
        const { productDetails } = store;
        return setBundleProductAnalytics(productDetails, pricingDetails, collectionView);
    }

    static getCartAddAnalytics(
        store,
        pricingDetails,
        productOptions,
        deliveryMethod,
        cartItems,
        clickPosition,
        maxQuantityMsgDisplay,
        deliveryDayType,
        deliveryDays,
        sourceMiniPDP
    ) {
        const { addToCartInterstitialDetails, miniPDPcartInterstitial, virtualTryOn } = store;
        const productDetails = selectProductDetailsWrapper(sourceMiniPDP)(store);
        const inventory = selectInventoryWrapper(sourceMiniPDP)(store);
        const productOffering = selectProductOfferingWrapper(sourceMiniPDP)(store);
        const selectedQuantity = selectSelectedQuantityWrapper(sourceMiniPDP)(store);

        let orderItem = {};
        const cartInterstitialDetails = sourceMiniPDP
            ? miniPDPcartInterstitial
            : addToCartInterstitialDetails;
        const cartTotal = findCartTotal(cartInterstitialDetails.totals);
        /* istanbul ignore next */
        if (cartInterstitialDetails.bag && !sourceMiniPDP) {
            orderItem = find(cartInterstitialDetails.bag.items, { sku: { id: cartItems[0].skuId } })
                ? find(cartInterstitialDetails.bag.items, { sku: { id: cartItems[0].skuId } })
                : cartInterstitialDetails.bag.items[0];
        }
        const featureFlags = selectFeatureFlags(store);
        const productInfo = setCartAddAnalytics(
            productDetails,
            pricingDetails,
            productOptions,
            selectedQuantity,
            inventory,
            productOffering,
            deliveryMethod,
            clickPosition,
            orderItem,
            maxQuantityMsgDisplay,
            deliveryDayType,
            deliveryDays,
            featureFlags,
            virtualTryOn
        );
        const cart = { attributes: { cartTotal } };
        const product = [];
        product.push({
            productInfo,
        });
        /* istanbul ignore else */
        if (!sourceMiniPDP) {
            cartItems[0].childItems.forEach((childItem) => {
                const warrantyPrice = find(productOptions.selectedProductLot[0].warranties, {
                    itemId: childItem.skuId,
                });
                const lotId = (childItem.skuId || '').slice(0, 7);
                let productCurrentSellingPrice = '0.00';
                /* istanbul ignore next */
                if (warrantyPrice && warrantyPrice.price) {
                    productCurrentSellingPrice = Number(warrantyPrice.price).toFixed(2);
                }
                product.push({
                    productInfo: {
                        productLotId: [lotId],
                        productPPID: childItem.ppId,
                        productQuantity: childItem.quantity,
                        productSKU: childItem.skuId,
                        productCurrentSellingPrice,
                    },
                });
            });
        }
        return { product, cart };
    }

    static getBundleCartAddAnalytics(
        store,
        selectedBundleSkus,
        deliveryMethod,
        cartItems,
        clickPosition,
        deliveryDayType,
        deliveryDays
    ) {
        const {
            productDetails,
            bundlePrice,
            bundleQuantity,
            bundleInventory,
            bundleUpSell,
            bundleDeliveryDate,
            deliveryZipcode,
            virtualTryOn,
        } = store;
        const product = [];
        const bundleEstimatedDeliveryDate =
            bundleDeliveryDate &&
            bundleDeliveryDate[deliveryZipcode] &&
            getBundleEstimatedDeliveryDate(bundleDeliveryDate[deliveryZipcode], cartItems);
        const productBundleFlag =
            productDetails.products.length === cartItems.length ? 'yes' : 'no';
        let cartTotal = '0.00';
        cartItems.forEach((cartItem) => {
            const bundleProductDetails = find(
                productDetails.products,
                (bundleProduct) => bundleProduct.base.id === cartItem.ppId
            );
            const bundleProductOptions = selectedBundleSkus[cartItem.ppId];
            const selectedSilverOfferingItems = bundleUpSell && bundleUpSell[cartItem.ppId];
            const productInfo = setCartAddAnalytics(
                bundleProductDetails.product,
                bundlePrice.calcChildLevelPrice[cartItem.ppId],
                bundleProductOptions,
                bundleQuantity[cartItem.ppId],
                bundleInventory.inventory
                    ? filter(bundleInventory.inventory, { id: cartItem.skuId })
                    : [],
                {},
                deliveryMethod,
                clickPosition,
                {},
                {},
                deliveryDayType,
                deliveryDays,
                virtualTryOn
            );
            product.push({
                productInfo: {
                    ...productInfo,
                    productBundleFlag,
                    productInStock: bundleProductOptions.isProductInStock
                        ? 'in stock'
                        : 'out of stock',
                    productEstimatedDelivery: bundleEstimatedDeliveryDate,
                },
            });
            cartItem.childItems.forEach((childItem) => {
                const warrantyPrice = find(bundleProductOptions.selectedProductLot[0].warranties, {
                    itemId: childItem.skuId,
                });
                const lotId = (childItem.skuId || '').slice(0, 7);
                const setSilverOfferingItemsPrice = selectedSilverOfferingItems
                    ? find(selectedSilverOfferingItems, { id: childItem.skuId })
                    : {};
                let productCurrentSellingPrice = '0.00';
                if (warrantyPrice && warrantyPrice.price) {
                    productCurrentSellingPrice = Number(warrantyPrice.price).toFixed(2);
                } else if (setSilverOfferingItemsPrice && setSilverOfferingItemsPrice.price) {
                    /* istanbul ignore next */
                    productCurrentSellingPrice = Number(setSilverOfferingItemsPrice.price).toFixed(
                        2
                    );
                }
                product.push({
                    productInfo: {
                        productLotId: [lotId],
                        productPPID: childItem.ppId,
                        productQuantity: childItem.quantity,
                        productSKU: childItem.skuId,
                        productCurrentSellingPrice,
                    },
                });
            });
            cartTotal = (
                Number(productInfo.productQuantity) *
                    Number(productInfo.productCurrentSellingPrice) +
                Number(cartTotal)
            ).toFixed(2);
        });
        const cart = { attributes: { cartTotal } };
        return { product, cart };
    }

    static getCartUpdateAnalytics(store, pricingDetails, productOptions, payload, sourceMiniPDP) {
        const { silverBundle, addToCartInterstitialDetails, miniPDPcartInterstitial } = store;
        const productDetails = selectProductDetailsWrapper(sourceMiniPDP)(store);
        const inventory = selectInventoryWrapper(sourceMiniPDP)(store);
        const productOffering = selectProductOfferingWrapper(sourceMiniPDP)(store);
        const selectedQuantity = selectSelectedQuantityWrapper(sourceMiniPDP)(store);
        const featureFlags = selectFeatureFlags(store);
        const cartInterstitialDetails = sourceMiniPDP
            ? miniPDPcartInterstitial
            : addToCartInterstitialDetails;
        const cartTotal = cartInterstitialDetails
            ? findCartTotal(cartInterstitialDetails.totals)
            : '0.00';
        const cart = { attributes: { cartTotal } };
        const product = setCartUpdateAnalytics(
            productDetails,
            pricingDetails,
            productOptions,
            selectedQuantity,
            inventory,
            productOffering,
            silverBundle,
            payload,
            featureFlags,
            addToCartInterstitialDetails
        );
        return { product, cart };
    }

    static getProductOOSAnalytics(store, pricingDetails, productOptions, sourceMiniPDP) {
        const productDetails = selectProductDetailsWrapper(sourceMiniPDP)(store);
        return setProductOOSAnalytics(productDetails, pricingDetails, productOptions);
    }
}

export default PDPAnalytics;
